import React, { useCallback, useState } from 'react';
import { grey } from '@material-ui/core/colors/';
import {
	makeStyles,
	AppBar,
	Backdrop,
	Box,
	Button,
	CircularProgress,
	Divider,
	Drawer,
	IconButton,
	ImageList,
	ImageListItem,
	ImageListItemBar,
	Toolbar,
	Typography,
} from '@material-ui/core';
import { ArrowBack, Close, InsertDriveFile, StopScreenShare } from '@material-ui/icons';
import Dropzone from 'react-dropzone';
import { Auth } from 'aws-amplify';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
	shareCanvasImgState,
	sharedAudiosState,
	sharedImageState,
	uploadedImagesState,
} from '../../../common/recoil/Share/atom';
import { addNewFile } from '../../../common/recoil/Share/selectors';
import { currentLibraryBreadCrumbState } from '../../../common/recoil/Library/libraryAtom';
import { currentLibraryContentSelector } from '../../../common/recoil/Library/librarySelector';
import { CHAT_EVENTS, LIBRARY_ITEMS_TYPES } from '../../../common/constants';
import { useChat } from '../../../common/hooks';
import { uploadFile } from '../../../common/services/libraryFilesRequests';
import { findAndAddElement } from '../../../common/services/libraryUtils';

const useStyles = makeStyles((theme) => ({
	folderTree: {
		backgroundColor: grey[200],
		height: '220px',
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'space-around',
		overflow: 'hidden',
	},
	dropZone: {
		border: `dashed ${grey[500]}`,
		padding: '5px',
		margin: '5px',
		height: '200px',
	},
	imageList: {
		width: 500,
		height: 450,
	},
	fileIcon: {
		width: '50%',
		height: '50%',
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
	stopShareImage: {
		position: 'absolute',
	},
	menuButton: {
		marginRight: theme.spacing(2),
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
}));

const LibraryContent = () => {
	const classes = useStyles();
	const [uploadedImages, setUploadedImages] = useRecoilState(uploadedImagesState);
	const [sharedImage, setSharedImage] = useRecoilState(sharedImageState);
	const [sharedAudios, setSharedAudios] = useRecoilState(sharedAudiosState);
	const [shareCanvasImg, setShareCanvasImg] = useRecoilState(shareCanvasImgState);
	const [filesUploaded, setNewFile] = useRecoilState(addNewFile);
	const [uploadingFile, setUploadingFile] = useState(false);
	const { messages, sendMessage } = useChat();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const [currentTab, setCurrentTab] = useState(2);
	const [currentLibraryContent, setCurrentLibraryContent] = useRecoilState(
		currentLibraryContentSelector
	);
	const currentLibraryBreadCrumb = useRecoilValue(currentLibraryBreadCrumbState);

	const { t, i18n } = useTranslation();

	const showError = (message) => {
		enqueueSnackbar(message, {
			variant: 'error',
			anchorOrigin: {
				vertical: 'bottom',
				horizontal: 'center',
			},
			action: (key) => (
				<Button onClick={onClickDismiss(key)}>
					<Close color={'white'}></Close>
				</Button>
			),
			preventDuplicate: true,
		});
	};

	const onClickDismiss = (key) => () => {
		closeSnackbar(key);
	};

	const onDrop = useCallback(
		async (acceptedFiles) => {
			acceptedFiles.forEach(async (file) => {
				const reader = new FileReader();

				reader.onabort = () => console.log('file reading was aborted');
				reader.onerror = () => console.log('file reading has failed');
				reader.onload = () => {
					const binaryStr = reader.result;
				};
				reader.readAsDataURL(file);
				try {
					setUploadingFile(true);
					const newFile = await uploadFile(file);
					if (newFile) {
						setNewFile(newFile);
					}
					setUploadingFile(false);
					addImageToFolder(newFile, file.name);
				} catch (e) {
					console.log('error', e);
					setUploadingFile(false);
				}
			});
		},
		[currentLibraryContent, currentLibraryBreadCrumb]
	);

	const addImageToFolder = (file, name) => {
		let currentLibrary = { ...currentLibraryContent };
		currentLibrary.children = [...currentLibrary.children];
		currentLibrary = findAndAddElement(
			currentLibrary,
			currentLibraryBreadCrumb.slice(1),
			name,
			showError,
			LIBRARY_ITEMS_TYPES.FILE,
			file
		);
		setCurrentLibraryContent(currentLibrary);
	};

	const onImgClick = (img) => {
		if (img.isImage) {
			if (img.key === sharedImage.key) {
				stopShareImage();
			} else {
				setSharedImage(img);
				setShareCanvasImg(img.url);
				const shareImgMessage = {
					type: CHAT_EVENTS.SHARE_S3_IMAGE,
					img: img.url,
				};
				sendMessage(shareImgMessage);
			}
		} else if (img.isAudio) {
			const tempAudios = [...sharedAudios];
			if (!sharedAudios.some((x) => x.key === img.key)) {
				tempAudios.push(img);
				setSharedAudios(tempAudios);
				const shareImgMessage = {
					type: CHAT_EVENTS.SHARE_S3_AUDIO,
					sharedAudio: tempAudios,
				};
				sendMessage(shareImgMessage);
			}
		}
	};

	const stopShareImage = () => {
		setShareCanvasImg(null);
		setSharedImage(null);
		const shareImgMessage = {
			type: CHAT_EVENTS.SHARE_S3_IMAGE,
			img: null,
		};
		sendMessage(shareImgMessage);
	};

	return (
		<Box className={classes.folderTree}>
			<Dropzone onDrop={onDrop}>
				{({ getRootProps, getInputProps }) => (
					<section className={classes.dropZone}>
						<div {...getRootProps()}>
							<input {...getInputProps()} />
							<p>Drag 'n' drop some files here, or click to select files</p>
						</div>
					</section>
				)}
			</Dropzone>
			<Backdrop className={classes.backdrop} open={uploadingFile}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</Box>
	);
};

export default LibraryContent;
